<template>
    <div class="record-overview position-relative loading-content-container">
        <div v-if="isLoading" class="loading-overlay d-flex justify-content-center pt-5">
            <csp-loading-indicator/>
        </div>
        <div v-if="loaded">
            <b-row class="mb-4">
                <template v-if="isRegressClaim">
                    <csp-record-claimant :claimant-data="claimantData" :cols="4"/>
                    <csp-record-defendant :cols="5" :defendant-data="defendantData"/>
                </template>
                <csp-record-customer-and-vehicle v-else :claim-type="claimType" :cols="9" :customer-data="customerData"
                    :vehicle-data="vehicleData"/>
                <csp-desktop-info-card :cols="3" icon="inticon-autoclaim_document_accident" title="Dokumente">
                    <b-row>
                        <b-col cols="12">
                            <div v-if="filesData.groups.length === 0" class="font-weight-bold">
                                Noch keine Dokumente vorhanden
                            </div>
                            <div v-else>
                                <div v-for="group in filesData.groups" :key="group.id" class="mb-3">
                                    <div class="font-weight-bold mb-1">
                                        {{ group.name }}
                                    </div>
                                    <div v-for="file in group.files" :key="file.id" @click="downloadFile(file.id)">
                                        <a :href="buildFileDownloadUrl(file.id)">{{ file.name }}</a>
                                    </div>
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                </csp-desktop-info-card>
            </b-row>
            <b-row class="mb-4">
                <csp-record-customer-and-vehicle v-if="isRegressClaim" :claim-type="claimType" :cols="9"
                    :customer-data="customerData" :vehicle-data="vehicleData"/>
                <csp-desktop-info-card v-else-if="adversaryData" :cols="6" icon="inticon-autoclaim_insurance"
                    title="Gegner und Versicherung">
                    <b-row>
                        <b-col cols="6">
                            <div class="focus-text">
                                {{ adversaryData.fullNameWithSalutation }}
                            </div>
                            <div>
                                {{ adversaryData.address }}
                            </div>
                        </b-col>
                        <b-col cols="6">
                            <b-row>
                                <b-col cols="6">
                                    <div v-if="adversaryData.mobile">
                                        <i class="inticon-phone value-icon"/>
                                        <span>{{ adversaryData.mobile }}</span>
                                    </div>
                                </b-col>
                                <b-col cols="6">
                                    <div v-if="adversaryData.phone">
                                        <i class="inticon-phone value-icon"/>
                                        <span>{{ adversaryData.phone }}</span>
                                    </div>
                                </b-col>
                            </b-row>
                            <b-row>
                                <div v-if="adversaryData.email">
                                    <i class="messages value-icon"/>
                                    <span>{{ adversaryData.email }}</span>
                                </div>
                            </b-row>
                        </b-col>
                    </b-row>
                    <hr>
                    <b-row>
                        <b-col cols="4">
                            <csp-license-place
                                :region="adversaryData.licensePlate.region"
                                :rest="adversaryData.licensePlate.rest"/>
                        </b-col>
                        <b-col cols="4">
                            <div class="font-weight-bold">
                                Versicherung
                            </div>
                            <span v-if="adversaryData.insurance.companyName">
                                {{ adversaryData.insurance.companyName }},
                                {{ adversaryData.insurance.address }}
                            </span>
                        </b-col>
                        <b-col cols="4">
                            <div v-if="adversaryData.insurance.phone">
                                <i class="inticon-phone value-icon"/>
                                <span>{{ adversaryData.insurance.phone }}</span>
                            </div>
                            <div v-if="adversaryData.insurance.reference">
                                <i class="inticon-documents value-icon"/>
                                <span>{{ adversaryData.insurance.reference }}</span>
                            </div>
                        </b-col>
                    </b-row>
                </csp-desktop-info-card>
                <csp-desktop-info-card v-if="comprehensiveInsuranceData" :cols="6" icon="inticon-autoclaim_insurance"
                    title="Versicherung">
                    <b-row>
                        <b-col cols="8">
                            <div class="font-weight-bold">
                                Versicherung
                            </div>
                            <span v-if="comprehensiveInsuranceData.companyName">
                                {{ comprehensiveInsuranceData.companyName }}<br>
                                {{ comprehensiveInsuranceData.address }}
                            </span>
                        </b-col>
                        <b-col cols="4">
                            <div v-if="comprehensiveInsuranceData.phone">
                                <i class="inticon-phone value-icon"/>
                                <span>{{ comprehensiveInsuranceData.phone }}</span>
                            </div>
                            <div v-if="comprehensiveInsuranceData.insuranceNumber">
                                <i class="inticon-documents value-icon"/>
                                <span>{{ comprehensiveInsuranceData.insuranceNumber }}</span>
                            </div>
                        </b-col>
                    </b-row>
                    <b-row class="mt-3">
                        <b-col cols="6">
                            <div>
                                Selbstbeteiligung
                            </div>
                            <div class="d-flex justify-content-between w-100">
                                <span>Vollkasko:</span>
                                <span>{{ comprehensiveInsuranceData.copaymentFully }}</span>
                                <span>Teilkasko:</span>
                                <span>{{ comprehensiveInsuranceData.copaymentPartially }}</span>
                            </div>
                        </b-col>
                    </b-row>
                </csp-desktop-info-card>
                <csp-desktop-info-card v-if="showAccidentData" :cols="3" icon="inticon-autoclaim_pin" title="Unfallort">
                    <b-row>
                        <b-col>
                            <div class="font-weight-bold">
                                Unfallort
                            </div>
                            <span>
                                {{ recordData.accidentAddress }}
                            </span>
                            <br>
                            <br>
                            <div class="font-weight-bold">
                                Unfalldatum
                            </div>
                            <span>
                                {{ recordData.accidentDate }}
                            </span>
                        </b-col>
                    </b-row>
                </csp-desktop-info-card>
                <csp-desktop-info-card :cols="3" icon="inticon-autoclaim_billing" title="Rechnungen">
                    <b-row>
                        <b-col>
                            <div class="invoice-meters">
                                <div class="invoice-meter">
                                    <span class="title">Reparatur</span>
                                    <span class="percentage">{{ costUnitsData.repair.percentPayed }}</span>
                                    <br>
                                    <span class="amount">Gesamt: {{ costUnitsData.repair.netFormatted }}</span>
                                    <b-progress class="repair-bar" height="8px" :max="costUnitsData.repair.barMax"
                                        :value="costUnitsData.repair.barValue"/>
                                </div>
                                <div class="invoice-meter">
                                    <span class="title">Mietwagen</span>
                                    <span class="percentage">{{ costUnitsData.rentVehicle.percentPayed }}</span>
                                    <br>
                                    <span class="amount">Gesamt: {{ costUnitsData.rentVehicle.netFormatted }}</span>
                                    <b-progress class="rentVehicle"
                                        height="8px"
                                        :max="costUnitsData.rentVehicle.barMax"
                                        :value="costUnitsData.rentVehicle.barValue"/>
                                </div>
                            </div>
                        </b-col>
                    </b-row>
                </csp-desktop-info-card>
            </b-row>
            <b-row class="mb-4">
                <b-col cols="12">
                    <csp-record-notes
                        :default-page-options="[5]"
                        :default-per-page="5"
                        :disable-filters="true"
                        :disable-pagination="true">
                        <template v-slot:below-body>
                            <br>
                            <p>Anzeige der 5 neuesten Nachrichten</p>
                        </template>
                    </csp-record-notes>
                </b-col>
            </b-row>
            <b-row v-if="firmRemarksData" class="mb-4">
                <csp-desktop-info-card :cols="12" icon="inticon-autoclaim_info" title="Firmeninfos">
                    <b-row>
                        <b-col cols="2">
                            <p><b>Kommunikation RA</b></p>
                            <p style="white-space: pre-line">
                                {{ firmRemarksData.remarksRequestLawyer }}
                            </p>
                        </b-col>
                        <b-col cols="2">
                            <p><b>Kommunikation Service</b></p>
                            <p style="white-space: pre-line">
                                {{ firmRemarksData.remarksCommunicationService }}
                            </p>
                        </b-col>
                        <b-col cols="2">
                            <p><b>Kommunikation Buchhaltung</b></p>
                            <p style="white-space: pre-line">
                                {{ firmRemarksData.remarksAccounting }}
                            </p>
                        </b-col>
                        <b-col cols="2">
                            <p><b>Kommunikation Geschädigter</b></p>
                            <p style="white-space: pre-line">
                                {{ firmRemarksData.remarksAdversary }}
                            </p>
                        </b-col>
                        <b-col cols="2">
                            <p><b>Partnerversicherung</b></p>
                            <p style="white-space: pre-line">
                                {{ firmRemarksData.remarksPartnerInsurance }}
                            </p>
                        </b-col>
                        <b-col cols="2">
                            <p><b>Sonstiges</b></p>
                            <p style="white-space: pre-line">
                                {{ firmRemarksData.remarksOther }}
                            </p>
                        </b-col>
                    </b-row>
                </csp-desktop-info-card>
            </b-row>
        </div>
    </div>
</template>

<script>
    import Container from '@services/Container';
    import '@desktop/scss/components/record/overview.scss';
    import FormDataMixin from '@desktop/components/mixins/FormDataMixin';
    import ClaimTypeEnum from '@js/enums/damage/ClaimTypeEnum';

    const container = Container.getInstance();
    const desktopRecordRepository = container.getDesktopRecordRepository();
    const recordRepository = container.getRecordRepository();

    export default {
        name: 'RecordOverview',
        components: {
            'csp-license-place': () => import('@desktop/components/record/vehicle/LicensePlate.vue'),
            'csp-record-notes': () => import('@desktop/components/record/Notes.vue'),
            'csp-record-customer-and-vehicle': () => import(
                '@desktop/components/record/overview/CustomerAndVehicle.vue'
            ),
            'csp-record-claimant': () => import('@desktop/components/record/overview/Claimant.vue'),
            'csp-record-defendant': () => import('@desktop/components/record/overview/Defendant.vue'),
        },
        mixins: [FormDataMixin],
        data() {
            return {
                recordId: this.$route.params.recordId,
                recordData: null,
                customerData: null,
                vehicleData: null,
                adversaryData: null,
                comprehensiveInsuranceData: null,
                claimantData: null,
                defendantData: null,
                costUnitsData: null,
                filesData: null,
                firmRemarksData: null,
                loaded: false,
                isLoading: true,
            };
        },
        computed: {
            claimType() {
                return this.recordData?.claimType;
            },
            isRegressClaim() {
                return this.claimType === ClaimTypeEnum.REGRESS;
            },
            showAccidentData() {
                return !this.isRegressClaim;
            },
        },
        created() {
            this.loadRecord();
        },
        methods: {
            loadRecord() {
                this.isLoading = true

                desktopRecordRepository.loadOverview(this.recordId)
                    .then(response => {
                        this.loaded = true
                        this.isLoading = false

                        this.setRecordData(response.data.attributes)
                    })
            },
            buildFileDownloadUrl(fileId) {
                return recordRepository.buildFileDownloadUrlDownload(this.recordId, fileId);
            },
            setRecordData(recordData) {
                this.recordData = recordData;
                this.customerData = recordData.customer.data.attributes;
                this.vehicleData = recordData.vehicle.data.attributes;
                this.costUnitsData = recordData.costUnits.data.attributes;
                this.filesData = recordData.files.data.attributes;
                if (recordData.firmRemarks?.data?.attributes) {
                    this.firmRemarksData = recordData.firmRemarks.data.attributes;
                }
                if (recordData.adversary?.data?.attributes) {
                    this.adversaryData = recordData.adversary.data.attributes;
                }
                if (recordData.comprehensiveInsurance?.data?.attributes) {
                    this.comprehensiveInsuranceData = recordData.comprehensiveInsurance.data.attributes;
                }
                if (recordData.claimant?.data?.attributes) {
                    this.claimantData = recordData.claimant.data.attributes;
                }
                if (recordData.defendant?.data?.attributes) {
                    this.defendantData = recordData.defendant.data.attributes;
                }
            },
        },
    }
</script>
